import React, { useMemo, useState } from 'react';
import { BsCaretDownFill, BsCaretRightFill, BsPrescription, BsFileEarmarkMedical, BsPerson, BsPeople, BsCapsule, BsActivity } from "react-icons/bs";

const PrescriptionKPI = ({ prescriptions = [], filteredPrescriptions = [], showKPIs = true }) => {
  const [showKPI, setShowKPI] = useState(showKPIs);

  // Ensure filteredPrescriptions is used if it exists, otherwise fall back to all prescriptions
  const effectivePrescriptions = useMemo(() => {
    // If filteredPrescriptions is provided and different from all prescriptions, use it
    if (filteredPrescriptions && filteredPrescriptions.length > 0 && 
        filteredPrescriptions.length !== prescriptions.length) {
      return filteredPrescriptions;
    }
    // Otherwise use the full dataset
    return prescriptions;
  }, [prescriptions, filteredPrescriptions]);

  // Calculate all KPIs based on current effective prescriptions
  const kpis = useMemo(() => {
    // No data case
    if (!effectivePrescriptions || effectivePrescriptions.length === 0) {
      return {
        totalPrescriptions: 0,
        totalMedicines: 0,
        uniqueMedicines: 0,
        totalPatients: 0,
        totalDoctors: 0,
        avgMedicinesPerPrescription: 0,
        totalTests: 0
      };
    }

    // Count unique entities
    const uniquePatients = new Set();
    const uniqueDoctors = new Set();
    const uniqueMedicineIds = new Set();
    let totalMedicines = 0;
    let totalTests = 0;

    effectivePrescriptions.forEach(prescription => {
      // Add unique patient and doctor IDs
      if (prescription.patient && prescription.patient._id) {
        uniquePatients.add(prescription.patient._id);
      }
      
      if (prescription.doctor && prescription.doctor._id) {
        uniqueDoctors.add(prescription.doctor._id);
      }

      // Count medicines and unique medicines
      if (prescription.prescribeMedicine && Array.isArray(prescription.prescribeMedicine)) {
        totalMedicines += prescription.prescribeMedicine.length;
        
        // Add unique medicine IDs to set
        prescription.prescribeMedicine.forEach(med => {
          if (med.medicine) {
            uniqueMedicineIds.add(med.medicine);
          }
        });
      }

      // Count tests
      if (prescription.prescribeTests && Array.isArray(prescription.prescribeTests)) {
        totalTests += prescription.prescribeTests.length;
      }
    });

    return {
      totalPrescriptions: effectivePrescriptions.length,
      totalMedicines,
      uniqueMedicines: uniqueMedicineIds.size,
      totalPatients: uniquePatients.size,
      totalDoctors: uniqueDoctors.size,
      avgMedicinesPerPrescription: (totalMedicines / effectivePrescriptions.length).toFixed(1),
      totalTests
    };
  }, [effectivePrescriptions]);

  // Format number with commas for thousands
  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  };

  // Toggle KPI visibility
  const toggleKPI = () => setShowKPI(prev => !prev);

  // Determine if filters are active
  const isFiltered = filteredPrescriptions && 
                    prescriptions && 
                    filteredPrescriptions.length !== prescriptions.length &&
                    filteredPrescriptions.length > 0;

  // Define the KPI cards data
  const kpiCards = [
    { 
      title: 'Total', 
      value: kpis.totalPrescriptions,
      icon: <BsPrescription />, 
      iconColor: '#f5f2e8'
    },
    { 
      title: 'Doctors', 
      value: kpis.totalDoctors,
      icon: <BsPerson />, 
      iconColor: '#e8f4fd'
    },
    { 
      title: 'Patients', 
      value: kpis.totalPatients,
      icon: <BsPeople />, 
      iconColor: '#fde8f4'
    },
    { 
      title: 'Medicines', 
      value: kpis.uniqueMedicines,
      icon: <BsCapsule />, 
      iconColor: '#e8f5e8'
    },
    { 
      title: 'Avg Med.', 
      value: kpis.avgMedicinesPerPrescription,
      icon: <BsActivity />, 
      iconColor: '#f0e8fd'
    },
    { 
      title: 'Tests', 
      value: kpis.totalTests,
      icon: <BsFileEarmarkMedical />, 
      iconColor: '#fff4e8'
    }
  ];

  return (
    <>
      {/* <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
        <div className="d-flex align-items-center">
          <h5 className="mb-0 me-3">Dashboard / Campaign</h5>
          <button onClick={toggleKPI} className="btn text-primary p-0">
            {showKPI ? 'Hide KPI ' : 'Show KPI '}
            {showKPI ? <BsCaretDownFill size={12} /> : <BsCaretRightFill size={12} />}
          </button>
        </div>
        {isFiltered && (
          <div className="badge bg-info text-white">
            Showing filtered data ({filteredPrescriptions.length} of {prescriptions.length})
          </div>
        )}
      </div> */}
      
      {showKPI && (
        <div className="row gy-3 introbx_row">
          {kpiCards.map((kpi, index) => (
            <div key={index} className="col">
              <div className="d-flex align-items-center gap-2 p-3 rounded-10 shadow-sm">
                <div className="icon flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle" style={{ width: '2rem', height: '2rem', backgroundColor: '#E4E4FF', color: '#8381FF' }}>
                  <span className="d-block material-symbols-outlined fs-lg">{kpi.icon}</span>
                </div>
                <div className="info flex-fill">
                  <p className="fs-sm text-gray mb-0">{kpi.title}</p>
                  <p className="lh-1 fw-semibold">{formatNumber(kpi.value)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default PrescriptionKPI;